import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, fmtNum, errMsg } from "@finance/lib/api";
import Layout, { PageHeader } from "@finance/components/Layout";
import { Button } from "@finance/components/ui/button";
import { Input } from "@finance/components/ui/input";
import { Label } from "@finance/components/ui/label";
import { DateField } from "@finance/components/DateField";
import { SearchableSelect } from "@finance/components/SearchableSelect";
import { Plus, Trash, Paperclip, ArrowLeft } from "@phosphor-icons/react";
import { toast } from "sonner";

const emptyLine = () => ({ name: "", category_id: "", account_code: "1500", exclusive: "" });

export default function AssetLodgment() {
  const navigate = useNavigate();
  const [suppliers, setSuppliers] = useState([]);
  const [cats, setCats] = useState([]);
  const [assetAccts, setAssetAccts] = useState([]);
  const [f, setF] = useState({ supplier_id: "", date: new Date().toISOString().slice(0, 10), credit_days: "", vat_applicable: false, vat_rate: 18, vat_amount: "", efd_verification_code: "", efd_timestamp: "", notes: "" });
  const [lines, setLines] = useState([emptyLine()]);
  const [files, setFiles] = useState([]);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    api.get("/suppliers").then((r) => setSuppliers(r.data)).catch(() => {});
    api.get("/asset-categories").then((r) => setCats(r.data)).catch(() => {});
    api.get("/gl/accounts").then((r) => setAssetAccts((r.data || []).filter((a) => a.type === "Asset"))).catch(() => {});
  }, []);

  const totalExcl = lines.reduce((s, l) => s + (Number(l.exclusive) || 0), 0);
  const suggestedVat = f.vat_applicable ? Math.round(totalExcl * (Number(f.vat_rate) || 0)) / 100 : 0;
  useEffect(() => {
    setF((p) => ({ ...p, vat_amount: p.vat_applicable ? String(Math.round((lines.reduce((s, l) => s + (Number(l.exclusive) || 0), 0)) * (Number(p.vat_rate) || 0)) / 100) : "" }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [f.vat_applicable, f.vat_rate, totalExcl]);
  const vatAmount = f.vat_applicable ? (Number(f.vat_amount) || 0) : 0;
  const totalIncl = totalExcl + vatAmount;

  const setLine = (i, k, v) => setLines((ls) => ls.map((l, j) => (j === i ? { ...l, [k]: v } : l)));
  const addLine = () => setLines((ls) => [...ls, emptyLine()]);
  const delLine = (i) => setLines((ls) => (ls.length > 1 ? ls.filter((_, j) => j !== i) : ls));

  const valid = f.supplier_id && lines.some((l) => Number(l.exclusive) > 0);

  const save = async () => {
    if (!valid) return;
    setBusy(true);
    try {
      const payload = {
        supplier_id: f.supplier_id, date: f.date, notes: f.notes, credit_days: Number(f.credit_days) || 0,
        vat_applicable: f.vat_applicable, vat_rate: Number(f.vat_rate) || 0,
        vat_amount: f.vat_applicable ? Number(f.vat_amount) || 0 : 0,
        efd_verification_code: f.efd_verification_code, efd_timestamp: f.efd_timestamp,
        lines: lines.filter((l) => Number(l.exclusive) > 0).map((l) => ({ name: l.name, category_id: l.category_id || null, account_code: l.account_code || "1500", exclusive: Number(l.exclusive) })),
      };
      const { data } = await api.post("/asset-lodgments", payload);
      for (const file of files) {
        const fd = new FormData();
        fd.append("file", file);
        await api.post(`/asset-lodgments/${data.id}/documents`, fd);
      }
      toast.success("Asset lodged — posted to Fixed Assets and Accounts Payable");
      navigate("/assets");
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };

  return (
    <Layout>
      <PageHeader title="Asset Lodgment" subtitle="Record fixed assets from a supplier invoice — posts to Accounts Payable (no cash moves yet)" />
      <div className="p-8">
        <div className="mx-auto max-w-4xl space-y-5">
          <button className="inline-flex items-center gap-1 text-sm text-gray-500 hover:text-[#1B4383]" data-testid="lodge-back-btn" onClick={() => navigate("/assets")}><ArrowLeft size={16} />Back to Asset Register</button>
          {/* Invoice-level */}
          <div className="rounded-sm border border-gray-200 bg-white p-5">
            <div className="lx-label mb-3 text-gray-600">Supplier Invoice</div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label className="lx-label">Supplier <span className="text-red-500">*</span></Label>
                <SearchableSelect className="mt-1" testid="lodge-supplier" placeholder="Select supplier"
                  value={f.supplier_id} onValueChange={(v) => { const sup = suppliers.find((s) => s.id === v); setF({ ...f, supplier_id: v, credit_days: (sup && (sup.credit_days ?? sup.payment_terms)) != null ? String(sup.credit_days ?? sup.payment_terms) : f.credit_days }); }}
                  options={suppliers.map((s) => ({ value: s.id, label: s.name }))} />
              </div>
              <div><Label className="lx-label">Invoice Date</Label><DateField className="mt-1" data-testid="lodge-date" value={f.date} onChange={(e) => setF({ ...f, date: e.target.value })} /></div>
              <div><Label className="lx-label">Supplier Credit Days</Label><Input type="number" className="mt-1" data-testid="lodge-credit-days" placeholder="0" value={f.credit_days} onChange={(e) => setF({ ...f, credit_days: e.target.value })} /><div className="mt-0.5 text-[11px] text-gray-400">Due {f.date && f.credit_days !== "" ? new Date(new Date(f.date).getTime() + (Number(f.credit_days) || 0) * 86400000).toISOString().slice(0, 10) : f.date}</div></div>
            </div>
            <div className="mt-4 grid grid-cols-2 gap-4">
              <div><Label className="lx-label">EFD Verification Code</Label><Input className="mt-1" data-testid="lodge-efd-code" value={f.efd_verification_code} onChange={(e) => setF({ ...f, efd_verification_code: e.target.value })} /></div>
              <div><Label className="lx-label">EFD Timestamp</Label><Input inputMode="numeric" className="mt-1" data-testid="lodge-efd-ts" placeholder="e.g. 20260610093000" value={f.efd_timestamp} onChange={(e) => setF({ ...f, efd_timestamp: e.target.value.replace(/[^0-9]/g, "") })} /></div>
            </div>
            {totalExcl > 0 && (
              <div className="mt-4 flex items-center gap-2">
                <input type="checkbox" id="vat-app" data-testid="lodge-vat-toggle" checked={f.vat_applicable} onChange={(e) => setF({ ...f, vat_applicable: e.target.checked })} className="h-4 w-4 accent-[#1B4383]" />
                <Label htmlFor="vat-app" className="cursor-pointer">VAT applicable?</Label>
              </div>
            )}
            {totalExcl > 0 && f.vat_applicable && (
              <div className="mt-3 grid grid-cols-3 gap-4">
                <div><Label className="lx-label">VAT rate (%)</Label><Input type="number" className="mt-1" data-testid="lodge-vat-rate" value={f.vat_rate} onChange={(e) => setF({ ...f, vat_rate: e.target.value })} /></div>
                <div><Label className="lx-label">VAT amount</Label><Input type="number" className="mt-1" data-testid="lodge-vat-amount" value={f.vat_amount} onChange={(e) => setF({ ...f, vat_amount: e.target.value })} /><div className="mt-0.5 text-[11px] text-gray-400">Suggested TSh {fmtNum(suggestedVat)} — editable</div></div>
                <div><Label className="lx-label">Total (inclusive)</Label><Input type="number" className="mt-1" data-testid="lodge-total-input" value={totalIncl} onChange={(e) => setF({ ...f, vat_amount: String((Number(e.target.value) || 0) - totalExcl) })} /><div className="mt-0.5 text-[11px] text-gray-400">Excl + VAT — editable</div></div>
              </div>
            )}
          </div>

          {/* Asset lines */}
          <div className="rounded-sm border border-gray-200 bg-white p-5">
            <div className="mb-3 flex items-center justify-between">
              <div className="lx-label text-gray-600">Assets on this invoice</div>
              <Button variant="outline" size="sm" data-testid="lodge-add-line" onClick={addLine}><Plus size={14} className="mr-1" />Add asset</Button>
            </div>
            <div className="space-y-3">
              {lines.map((l, i) => (
                <div key={i} className="grid grid-cols-12 items-end gap-2" data-testid={`lodge-line-${i}`}>
                  <div className="col-span-4"><Label className="lx-label">Description</Label><Input className="mt-1" data-testid={`lodge-line-name-${i}`} value={l.name} onChange={(e) => setLine(i, "name", e.target.value)} /></div>
                  <div className="col-span-3"><Label className="lx-label">Category</Label>
                    <SearchableSelect className="mt-1" testid={`lodge-line-cat-${i}`} placeholder="Category"
                      value={l.category_id} onValueChange={(v) => setLine(i, "category_id", v)}
                      options={cats.map((c) => ({ value: c.id, label: c.name }))} />
                  </div>
                  <div className="col-span-3"><Label className="lx-label">GL Account</Label>
                    <SearchableSelect className="mt-1" testid={`lodge-line-acct-${i}`} placeholder="1500"
                      value={l.account_code} onValueChange={(v) => setLine(i, "account_code", v)}
                      options={assetAccts.map((a) => ({ value: a.code, label: `${a.code} · ${a.name}` }))} />
                  </div>
                  <div className="col-span-1"><Label className="lx-label">Excl.</Label><Input type="number" className="mt-1" data-testid={`lodge-line-amt-${i}`} value={l.exclusive} onChange={(e) => setLine(i, "exclusive", e.target.value)} /></div>
                  <div className="col-span-1 pb-1.5"><button className="text-gray-300 hover:text-red-500" data-testid={`lodge-line-del-${i}`} onClick={() => delLine(i)}><Trash size={15} /></button></div>
                </div>
              ))}
            </div>
            <div className="mt-4 flex justify-end gap-6 border-t border-gray-100 pt-3 text-sm">
              <div>Exclusive: <b className="lx-num" data-testid="lodge-total-excl">TSh {fmtNum(totalExcl)}</b></div>
              {f.vat_applicable && <div>VAT: <b className="lx-num" data-testid="lodge-total-vat">TSh {fmtNum(vatAmount)}</b></div>}
              <div>Payable to supplier: <b className="lx-num text-[#1B4383]" data-testid="lodge-total-incl">TSh {fmtNum(totalIncl)}</b></div>
            </div>
          </div>

          {/* Attachments */}
          <div className="rounded-sm border border-gray-200 bg-white p-5">
            <div className="lx-label mb-2 text-gray-600">Supporting Documents</div>
            <label className="inline-flex cursor-pointer items-center gap-2 rounded-sm border border-gray-300 px-3 py-1.5 text-sm hover:bg-gray-50" data-testid="lodge-file-label">
              <Paperclip size={14} /> Attach files
              <input type="file" multiple className="hidden" data-testid="lodge-file-input" onChange={(e) => setFiles(Array.from(e.target.files || []))} />
            </label>
            {files.length > 0 && <div className="mt-2 text-xs text-gray-600" data-testid="lodge-file-list">{files.map((f2) => f2.name).join(", ")}</div>}
          </div>

          <div className="flex justify-end gap-2">
            <Button variant="outline" onClick={() => navigate("/assets")}>Cancel</Button>
            <Button className="bg-[#1B4383] hover:bg-[#122E5C]" data-testid="lodge-save" disabled={!valid || busy} onClick={save}>{busy ? "Posting…" : "Lodge & Post to AP"}</Button>
          </div>
        </div>
      </div>
    </Layout>
  );
}
